import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button, EmptyState, Field, Input, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { usePermissions } from '../lib/session';
import { useSalesRegions, useSalesUsers } from '../components/DealerSales';
import type {
  DealerAgingReport,
  DealerCategoryReport,
  DealerCollectionsReport,
  DealerProductReport,
  DealerSalesReport,
} from '../lib/types';

/**
 * Bayi raporları (F7-10, ADR-115): satış, ürün, kategori (satış raporu yetkisi); vade dağılımı ve tahsilat (cari hesap
 * görme yetkisi). Bayi, temsilci veya bölge kırılımı; CSV (Türkçe Excel).
 */

type Report = 'sales' | 'products' | 'categories' | 'aging' | 'collections';
type GroupBy = 'company' | 'rep' | 'region';
const METHODS = ['bank_transfer', 'cash', 'card', 'cheque', 'note', 'other', 'order'] as const;

function istanbulToday() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Istanbul' }).format(new Date()).slice(0, 10);
}

export function DealerReportsPage() {
  const t = useT();
  const { can, dealerScoped } = usePermissions();
  const reports: Report[] = [
    ...(can('reports.sales.read') ? (['sales', 'products', 'categories'] as const) : []),
    ...(can('b2b.ledger.read') ? (['aging', 'collections'] as const) : []),
  ];
  const today = istanbulToday();
  const [report, setReport] = useState<Report>(reports[0] ?? 'sales');
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [groupBy, setGroupBy] = useState<GroupBy>('company');
  const [rep, setRep] = useState('');
  const [region, setRegion] = useState('');
  const filters = can('b2b.dealer.read') && !dealerScoped;
  const users = useSalesUsers(filters);
  const regions = useSalesRegions(filters);
  const params = new URLSearchParams();
  if (report !== 'aging') {
    if (from) params.set('from', from);
    if (to) params.set('to', to);
  }
  if (report === 'sales' || report === 'aging' || report === 'collections') params.set('groupBy', groupBy);
  if (rep) params.set('salesRepUserId', rep);
  if (region) params.set('salesRegionId', region);
  const path = `/v1/admin/reports/dealers/${report}`;
  const q = useQuery({
    queryKey: ['dealer-report', report, params.toString()],
    queryFn: () => api.get<unknown>(`${path}?${params}`),
    enabled: reports.includes(report),
  });
  const download = async () => {
    try {
      const { blob, fileName } = await api.blob(`${path}?${params}&format=csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? `bayi-${report}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    }
  };
  const grouped = report === 'sales' || report === 'aging' || report === 'collections';
  return (
    <PageFrame
      helpKey="page.dealer_reports"
      actions={
        reports.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <Button onClick={() => void download()}>{t('admin.dealerReports.csv')}</Button>
            <HelpTip k="dealer_reports.csv" />
          </span>
        )
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.dealerReports.report')} help={<HelpTip k="dealer_reports.report" />}>
          {(p) => (
            <Select {...p} value={report} onChange={(e) => setReport(e.target.value as Report)}>
              {reports.map((r) => (
                <option key={r} value={r}>
                  {t(`admin.dealerReports.reports.${r}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {report !== 'aging' && (
          <>
            <Field label={t('admin.dealerReports.from')} help={<HelpTip k="dealer_reports.period" />}>
              {(p) => (
                <Input {...p} type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
              )}
            </Field>
            <Field label={t('admin.dealerReports.to')}>
              {(p) => (
                <Input {...p} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
              )}
            </Field>
          </>
        )}
        {grouped && (
          <Field label={t('admin.dealerReports.groupBy')} help={<HelpTip k="dealer_reports.group_by" />}>
            {(p) => (
              <Select {...p} value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
                {(['company', 'rep', 'region'] as const).map((g) => (
                  <option key={g} value={g}>
                    {t(`admin.dealerReports.groups.${g}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {filters && (
          <>
            <Field label={t('admin.dealerReports.rep')} help={<HelpTip k="dealer_reports.rep" />}>
              {(p) => (
                <Select {...p} value={rep} onChange={(e) => setRep(e.target.value)}>
                  <option value="">{t('admin.dealerReports.all')}</option>
                  <option value="none">{t('admin.dealerReports.noRep')}</option>
                  {(users.data ?? []).map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.dealerReports.region')} help={<HelpTip k="dealer_reports.region" />}>
              {(p) => (
                <Select {...p} value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="">{t('admin.dealerReports.all')}</option>
                  <option value="none">{t('admin.dealerReports.noRegion')}</option>
                  {(regions.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </>
        )}
      </div>
      <p className="max-w-[80ch] text-sm text-ink-3">{t(`admin.dealerReports.notes.${report}`)}</p>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : report === 'sales' ? (
        <SalesTable data={q.data as DealerSalesReport} />
      ) : report === 'products' ? (
        <ProductsTable data={q.data as DealerProductReport} />
      ) : report === 'categories' ? (
        <CategoriesTable data={q.data as DealerCategoryReport} />
      ) : report === 'aging' ? (
        <AgingTable data={q.data as DealerAgingReport} />
      ) : (
        <CollectionsTable data={q.data as DealerCollectionsReport} />
      )}
    </PageFrame>
  );
}

function Table({
  caption,
  head,
  children,
  foot,
}: {
  caption: string;
  head: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full min-w-[48rem] text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-surface-2 text-ink-2">{head}</thead>
        <tbody>{children}</tbody>
        {foot && <tfoot className="border-t-2 border-line bg-surface-2 font-medium">{foot}</tfoot>}
      </table>
    </div>
  );
}

const th = 'px-3 py-2 text-start font-medium';
const thR = 'px-3 py-2 text-end font-medium';
const td = 'px-3 py-2';
const tdR = 'px-3 py-2 text-end tabular';

/** Kırılım satırının adı: bayi bağlantısı veya temsilci/bölge adı. */
function GroupCell({
  row,
  groupBy,
  ledger,
}: {
  row: { key: string; label: string | null; code: string | null };
  groupBy: GroupBy;
  ledger?: boolean;
}) {
  const t = useT();
  if (row.key === 'none' || !row.label)
    return <span className="text-ink-3">{t(`admin.dealerReports.none.${groupBy}`)}</span>;
  if (groupBy !== 'company') return <>{row.label}</>;
  return (
    <>
      {ledger ? (
        <Link
          to="/cari-hesaplar/$id"
          params={{ id: row.key }}
          className="font-medium text-pine hover:underline"
        >
          {row.label}
        </Link>
      ) : (
        <Link to="/bayiler/$id" params={{ id: row.key }} className="font-medium text-pine hover:underline">
          {row.label}
        </Link>
      )}
      {row.code && <span className="block text-ink-3">{row.code}</span>}
    </>
  );
}

function SalesTable({ data }: { data: DealerSalesReport }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const m = (n: number) => money(n, data.currency);
  if (!data.rows.length)
    return (
      <EmptyState
        title={t('admin.dealerReports.emptySales')}
        description={t('admin.dealerReports.emptyLead')}
      />
    );
  const many = data.groupBy !== 'company';
  return (
    <Table
      caption={t('admin.dealerReports.reports.sales')}
      head={
        <tr>
          <th className={th}>{t(`admin.dealerReports.groups.${data.groupBy}`)}</th>
          <th className={thR}>{t('admin.dealerReports.orders')}</th>
          {many && <th className={thR}>{t('admin.dealerReports.dealers')}</th>}
          <th className={thR}>{t('admin.dealerReports.gross')}</th>
          <th className={thR}>{t('admin.dealerReports.refunded')}</th>
          <th className={thR}>{t('admin.dealerReports.net')}</th>
          <th className={thR}>{t('admin.dealerReports.netExTax')}</th>
          <th className={thR}>{t('admin.dealerReports.average')}</th>
          <th className={th}>{t('admin.dealerReports.lastOrder')}</th>
        </tr>
      }
      foot={
        <tr>
          <td className={td}>{t('admin.dealerReports.total')}</td>
          <td className={tdR}>{data.totals.orders}</td>
          {many && <td className={tdR}>{data.totals.dealers}</td>}
          <td className={tdR}>{m(data.totals.grossMinor)}</td>
          <td className={tdR}>{m(data.totals.refundedMinor)}</td>
          <td className={tdR}>{m(data.totals.netMinor)}</td>
          <td className={tdR}>{m(data.totals.netExTaxMinor)}</td>
          <td />
          <td />
        </tr>
      }
    >
      {data.rows.map((r) => (
        <tr key={r.key} className="border-t border-line align-top">
          <td className={td}>
            <GroupCell row={r} groupBy={data.groupBy} />
          </td>
          <td className={tdR}>{r.orders}</td>
          {many && <td className={tdR}>{r.dealers}</td>}
          <td className={tdR}>{m(r.grossMinor)}</td>
          <td className={tdR}>{r.refundedMinor ? m(r.refundedMinor) : '—'}</td>
          <td className={tdR}>{m(r.netMinor)}</td>
          <td className={tdR}>{m(r.netExTaxMinor)}</td>
          <td className={tdR}>{m(r.averageMinor)}</td>
          <td className={td}>{r.lastOrderAt ? formatDate(r.lastOrderAt, false) : '—'}</td>
        </tr>
      ))}
    </Table>
  );
}

function ProductsTable({ data }: { data: DealerProductReport }) {
  const t = useT();
  const money = useMoney();
  if (!data.rows.length)
    return (
      <EmptyState
        title={t('admin.dealerReports.emptySales')}
        description={t('admin.dealerReports.emptyLead')}
      />
    );
  return (
    <Table
      caption={t('admin.dealerReports.reports.products')}
      head={
        <tr>
          <th className={th}>{t('admin.dealerReports.product')}</th>
          <th className={thR}>{t('admin.dealerReports.quantity')}</th>
          <th className={thR}>{t('admin.dealerReports.netExTax')}</th>
          <th className={thR}>{t('admin.dealerReports.orders')}</th>
          <th className={thR}>{t('admin.dealerReports.dealers')}</th>
        </tr>
      }
      foot={
        <tr>
          <td className={td}>{t('admin.dealerReports.total')}</td>
          <td className={tdR}>{data.totals.quantity}</td>
          <td className={tdR}>{money(data.totals.netExTaxMinor, data.currency)}</td>
          <td />
          <td />
        </tr>
      }
    >
      {data.rows.map((r) => (
        <tr key={r.key} className="border-t border-line align-top">
          <td className={td}>
            {r.title}
            <span className="block text-ink-3">{r.sku}</span>
          </td>
          <td className={tdR}>{r.quantity}</td>
          <td className={tdR}>{money(r.netExTaxMinor, data.currency)}</td>
          <td className={tdR}>{r.orders}</td>
          <td className={tdR}>{r.dealers}</td>
        </tr>
      ))}
    </Table>
  );
}

function CategoriesTable({ data }: { data: DealerCategoryReport }) {
  const t = useT();
  const money = useMoney();
  if (!data.rows.length)
    return (
      <EmptyState
        title={t('admin.dealerReports.emptySales')}
        description={t('admin.dealerReports.emptyLead')}
      />
    );
  return (
    <Table
      caption={t('admin.dealerReports.reports.categories')}
      head={
        <tr>
          <th className={th}>{t('admin.dealerReports.category')}</th>
          <th className={thR}>{t('admin.dealerReports.quantity')}</th>
          <th className={thR}>{t('admin.dealerReports.netExTax')}</th>
          <th className={thR}>{t('admin.dealerReports.orders')}</th>
          <th className={thR}>{t('admin.dealerReports.dealers')}</th>
        </tr>
      }
      foot={
        <tr>
          <td className={td}>{t('admin.dealerReports.productTotal')}</td>
          <td className={tdR}>{data.totals.quantity}</td>
          <td className={tdR}>{money(data.totals.netExTaxMinor, data.currency)}</td>
          <td />
          <td />
        </tr>
      }
    >
      {data.rows.map((r) => (
        <tr key={r.categoryId ?? 'none'} className="border-t border-line">
          <td className={td}>
            {r.name ?? <span className="text-ink-3">{t('admin.dealerReports.noCategory')}</span>}
          </td>
          <td className={tdR}>{r.quantity}</td>
          <td className={tdR}>{money(r.netExTaxMinor, data.currency)}</td>
          <td className={tdR}>{r.orders}</td>
          <td className={tdR}>{r.dealers}</td>
        </tr>
      ))}
    </Table>
  );
}

function AgingTable({ data }: { data: DealerAgingReport }) {
  const t = useT();
  const money = useMoney();
  const m = (n: number) => (n ? money(n, data.currency) : '—');
  if (!data.rows.length)
    return (
      <EmptyState
        title={t('admin.dealerReports.emptyAging')}
        description={t('admin.dealerReports.emptyLead')}
      />
    );
  const buckets = ['notDue', 'd1_30', 'd31_60', 'd61_90', 'd90plus'] as const;
  return (
    <Table
      caption={t('admin.dealerReports.reports.aging')}
      head={
        <tr>
          <th className={th}>{t(`admin.dealerReports.groups.${data.groupBy}`)}</th>
          {data.groupBy !== 'company' && <th className={thR}>{t('admin.dealerReports.dealers')}</th>}
          <th className={thR}>{t('admin.dealerReports.balance')}</th>
          {buckets.map((b) => (
            <th key={b} className={thR}>
              {t(`admin.dealerReports.buckets.${b}`)}
            </th>
          ))}
          <th className={thR}>{t('admin.dealerReports.bizimhesap')}</th>
          <th className={thR}>{t('admin.dealerReports.oldest')}</th>
        </tr>
      }
      foot={
        <tr>
          <td className={td}>{t('admin.dealerReports.total')}</td>
          {data.groupBy !== 'company' && <td className={tdR}>{data.totals.dealers}</td>}
          <td className={tdR}>{money(data.totals.balanceMinor, data.currency)}</td>
          {buckets.map((b) => (
            <td key={b} className={tdR}>
              {m(data.totals[b])}
            </td>
          ))}
          <td />
          <td />
        </tr>
      }
    >
      {data.rows.map((r) => (
        <tr key={r.key} className="border-t border-line align-top">
          <td className={td}>
            <GroupCell row={r} groupBy={data.groupBy} ledger />
          </td>
          {data.groupBy !== 'company' && <td className={tdR}>{r.dealers}</td>}
          <td className={tdR}>{money(r.balanceMinor, data.currency)}</td>
          {buckets.map((b) => (
            <td key={b} className={`${tdR} ${b !== 'notDue' && r[b] ? 'text-rose' : ''}`}>
              {m(r[b])}
            </td>
          ))}
          <td className={tdR}>
            {r.bizimhesapMinor === null ? '—' : money(r.bizimhesapMinor, data.currency)}
          </td>
          <td className={tdR}>
            {r.oldestDays === null ? '—' : t('admin.dealerReports.days', { days: r.oldestDays })}
          </td>
        </tr>
      ))}
    </Table>
  );
}

function CollectionsTable({ data }: { data: DealerCollectionsReport }) {
  const t = useT();
  const money = useMoney();
  const m = (n: number) => (n ? money(n, data.currency) : '—');
  if (!data.rows.length)
    return (
      <EmptyState
        title={t('admin.dealerReports.emptyCollections')}
        description={t('admin.dealerReports.emptyLead')}
      />
    );
  const used = METHODS.filter((k) => data.totals[k] !== 0);
  return (
    <Table
      caption={t('admin.dealerReports.reports.collections')}
      head={
        <tr>
          <th className={th}>{t(`admin.dealerReports.groups.${data.groupBy}`)}</th>
          <th className={thR}>{t('admin.dealerReports.count')}</th>
          <th className={thR}>{t('admin.dealerReports.collected')}</th>
          {used.map((k) => (
            <th key={k} className={thR}>
              {t(`admin.dealerReports.methods.${k}`)}
            </th>
          ))}
        </tr>
      }
      foot={
        <tr>
          <td className={td}>{t('admin.dealerReports.total')}</td>
          <td className={tdR}>{data.totals.count}</td>
          <td className={tdR}>{money(data.totals.totalMinor, data.currency)}</td>
          {used.map((k) => (
            <td key={k} className={tdR}>
              {m(data.totals[k])}
            </td>
          ))}
        </tr>
      }
    >
      {data.rows.map((r) => (
        <tr key={r.key} className="border-t border-line align-top">
          <td className={td}>
            <GroupCell row={r} groupBy={data.groupBy} ledger />
          </td>
          <td className={tdR}>{r.count}</td>
          <td className={tdR}>{money(r.totalMinor, data.currency)}</td>
          {used.map((k) => (
            <td key={k} className={tdR}>
              {m(r[k])}
            </td>
          ))}
        </tr>
      ))}
    </Table>
  );
}
